import type { Company } from '@/company'
import { CompanySelector } from '@/components/company/company-selector'
import { MonthSelectorWrapper } from '@/components/month-selector-wrapper'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getDeadlineCorporate, type DeadlineCorporate } from '@/deadline-corporate'
import { PageTitle } from '@/layout/page-title'
import { createFileRoute, useLoaderData, useRouter, useSearch } from '@tanstack/react-router'
import { ProgressDeadline } from '@/components/deadline-corporate/progress-deadline'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircleIcon } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TaskSelector } from '@/components/deadline-corporate/task-selector'
import { requireAuth } from '@/auth/requireAuth'
import { getClosingDate } from '@/utils/closing-date'


export const Route = createFileRoute('/(app)/_layout/key-deadline')({
  component: RouteComponent,
  beforeLoad: requireAuth,
  validateSearch: (search?: Record<string, unknown>): any => {
    // validate and parse the search params into a typed state
    const closingDate = getClosingDate();
    return {
      month: search?.month as number ?? closingDate.month, // Default to current month if not provided
      year: search?.year as number ?? closingDate.year,
      sapCode: search?.sapCode as string | null ?? null,
      groupBy: search?.groupBy as 'task' | 'company' ?? 'task', // Default to 'tasks' if not provided
      activityDescription: search?.activityDescription as string | null ?? null, // Default to null if not provided
    }
  },
  loaderDeps: ({ search: { month, year, sapCode, groupBy, activityDescription } }) => {
    return {
      month: month, // Default to current month if not provided
      year: year, // Default to current year if not provided
      sapCode: sapCode,
      groupBy: groupBy,  // Default to 'tasks' if not provided
      activityDescription: activityDescription // Default to null if not provided
    };
  },
  loader: async ({ deps }) => {
    const { month, year, sapCode, groupBy, activityDescription } = deps;

    const { data, error } = await getDeadlineCorporate({ month, year, sapCode, groupBy, activityDescription });
    return { activitiesData: { data, error } };
  },
  pendingComponent: () => <div className="text-center">Loading tasks...</div>,
})


function RouteComponent() {
  const { navigate } = useRouter();
  const search = useSearch({ from: '/(app)/_layout/key-deadline' });
  const { companiesData } = useLoaderData({ from: '/(app)/_layout' });
  const { activitiesData } = useLoaderData({ from: '/(app)/_layout/key-deadline' });
  const { groupBy } = useSearch({
    from: '/(app)/_layout/key-deadline'
  });

  const taskDescriptions = activitiesData?.data?.map((activity: DeadlineCorporate) => activity.description) ?? [];

  const onSelectCompany = (company: Company | null) => {

    // Navigate to the same route with updated search params
    navigate({
      // @ts-ignore
      search: (old) => {
        return {
          ...old,
          sapCode: company?.sapCode ?? null,
        }
      }
    })
  }

  const onSelectActivityDescription = (activityDescription: string | null) => {
    navigate({
      //@ts-ignore
      search: (old) => {
        return {
          ...old,
          activityDescription,
        }
      }
    })
  }

  const handleChangeGroupBy = (value: string) => {
    navigate({
      //@ts-ignore
      search: (old) => {
        return {
          ...old,
          groupBy: value,
        }
      }
    })
  }

  return (
    <div className="container max-w-[1900px] py-4 px-4 mx-auto">
      <PageTitle title="Key Deadline" rightActions={
        <div className="max-w-[350px]">
          <MonthSelectorWrapper initialMonth={search.month} initialYear={search.year} />
        </div>
      }
      />
      <div className="my-2 flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          <div>
            <Tabs value={groupBy} onValueChange={handleChangeGroupBy}>
              <TabsList>
                <TabsTrigger value={'task'}>By task</TabsTrigger>
                <TabsTrigger value={'company'}>By company</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex gap-2 max-w-[550px]">
            {groupBy === 'company' &&
              <CompanySelector
                companies={companiesData?.data ?? []}
                onSelectCompany={onSelectCompany}
              />
            }
            {
              groupBy === 'task' && (
                <TaskSelector
                  tasks={taskDescriptions}
                  onSelectTask={onSelectActivityDescription}
                />
              )
            }
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {
            activitiesData?.data?.map((activity: DeadlineCorporate, index: number) => {

              return (
                <Card key={`${activity.description}-${index}`}>
                  <CardHeader>
                    <CardTitle>{activity.description}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* <BarChart data={data} options={options} /> */}
                    <ProgressDeadline
                      completed={activity.completed}
                      notCompleted={activity.notCompleted}
                    />
                  </CardContent>
                </Card>
              )
            })
          }
          {
            activitiesData?.error &&
            <Alert variant="destructive">
              <AlertCircleIcon />
              <AlertTitle>Error downloading deadline activities.</AlertTitle>
              <AlertDescription>
                <p>{activitiesData.error.message}</p>
              </AlertDescription>
            </Alert>
          }
          {
            activitiesData?.data?.length === 0 &&
            <Alert>
              <AlertCircleIcon />
              <AlertTitle>No deadline activities found.</AlertTitle>
              <AlertDescription>
                <p>There are no deadline activities for the selected month and year.</p>
              </AlertDescription>
            </Alert>
          }

        </div>
      </div>
    </div>
  )
}
